// @erp/module-auth/fe — plain-fetch wrappers over the better-auth mount
// (browser-only, solid export condition; no JSX here — the screens live beside
// it as their own entries: ./sign-in, ./step-up, ./password-reset, ./invite,
// each wired by a depth-1 portal screen; auth-kit/fe owns SessionProvider/useCan).
//
// Paths verified against the 1.6.29 dist route definitions
// (dist/api/routes: "/sign-in/email", "/sign-up/email", "/sign-out"),
// prefixed by makeAuth's basePath '/auth'. Portals proxy /auth to the gateway
// (dev: 4801 → 4800), so relative URLs keep the cookie first-party.
// credentials:'include' — the opaque session cookie is the ONLY credential
// (DEV-GUIDE-005; never an Authorization header on the browser path).
//
// Typed result, never a throw: auth failure is a UI state, not an exception —
// mutations branch on `ok` and surface `error` verbatim.

export interface AuthResult {
  ok: boolean;
  error?: string;
  /**
   * better-auth's machine-readable error code, when the body carried one.
   *
   * ONE caller reads it: `STEP_UP_REQUIRED`, which is not a failed sign-in at
   * all — the password was right and a code is already in the person's inbox.
   * The login screens branch on this rather than on the message, because the
   * message is human copy and human copy gets rewritten.
   */
  code?: string;
}

/** The refusal that means "correct password, second step owed" (STEP-UP-POLICY.md). */
export const STEP_UP_REQUIRED = "STEP_UP_REQUIRED";

const post = async (
  path: string,
  body: Record<string, string>,
  /**
   * The human-check header (@erp/ui/turnstile's `captcha.headers()`).
   *
   * Passed in rather than read here: this file is the plain-fetch edge and owns
   * no UI, and the token belongs to the widget the form rendered. A call that
   * omits it is refused by the gateway on a real deployment, which is the point.
   */
  extraHeaders: Record<string, string> = {},
): Promise<AuthResult> => {
  try {
    const res = await fetch(path, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", ...extraHeaders },
      body: JSON.stringify(body),
    });
    if (res.ok) return { ok: true };
    let error = `Request failed (${res.status})`;
    let code: string | undefined;
    try {
      // TWO error shapes reach this path. better-auth's own APIError body is
      // flat — { code, message } — while anything the GATEWAY refuses first
      // (the human check, ADR-001 §7.2) arrives as { error: { code, message } }.
      // Reading only the first shape is how a Turnstile refusal used to render
      // as "Request failed (403)" to a person who could have simply reloaded.
      const data = (await res.json()) as {
        message?: unknown;
        code?: unknown;
        error?: { message?: unknown; code?: unknown };
      };
      const flat = data?.error && typeof data.error === "object" ? data.error : data;
      if (typeof flat?.message === "string" && flat.message) error = flat.message;
      if (typeof flat?.code === "string" && flat.code) code = flat.code;
    } catch {
      // non-JSON error body — the generic status message stands
    }
    return { ok: false, error, ...(code === undefined ? {} : { code }) };
  } catch {
    return { ok: false, error: "Network error — please try again" };
  }
};

export const signInEmail = (
  email: string,
  password: string,
  headers: Record<string, string> = {},
): Promise<AuthResult> => post("/auth/sign-in/email", { email, password }, headers);

/** Sign-up NEVER sends a role — role is server-assigned (see ../schemas). */
export const signUpEmail = (
  name: string,
  email: string,
  password: string,
  headers: Record<string, string> = {},
): Promise<AuthResult> => post("/auth/sign-up/email", { name, email, password }, headers);

export const signOutUser = (): Promise<AuthResult> => post("/auth/sign-out", {});
